import React, { FC, useState } from "react"
import { useHistory, useParams } from "react-router-dom"
import { reportError, reportErrorMessage } from "utils/ErrorReporting"
import CarrotClient from "carrot-api"
import { FormProvider, useForm } from "react-hook-form"
import { SendMessageForm } from "./SendMessageForm"
import { useDispatch, useSelector } from "react-redux"
import { getUserInfo } from "../../../reducers/userInfo"
import { signOut } from "../../../actions/loginActions"
import L10n from "../../../lib/carrot-l10n"
import Settings from "../../../utils/CarrotConfig"
import { HttpErrors } from "../../../utils/HttpErrors"
import { getBenefitConfiguration } from "../../../reducers/benefitConfiguration"
import { StringParam, useQueryParam } from "use-query-params"
import { getHeap } from "../../../utils/heap"
import {
  getInputOrSelectEmail,
  getPageSpecificDescriptionOrFallback,
  getPageSpecificHeadingOrFallback,
  messageTopicsToSubjectMap
} from "./SendMessageUtil"

const SendMessageFormContext = React.createContext<SendMessageContextProps>({})
const client = new CarrotClient(Settings.CARROT_BACKEND_URL)
const l = new L10n({ locale: "en-US" }).getBoundLocalize()

type SendMessageContextProps = {
  onSubmit?: () => Promise<void>
  subtitle?: string
  description?: string
  sent?: boolean
  employeeEmail?: string
  personalEmail?: string
  partnerEmail?: string
  sentFromPartnerEmail?: boolean
  options?: object[]
  onClickButton?: (onSubmit: () => Promise<void>) => Promise<void>
}

interface SendMessageProps {
  BenefitEnrollmentView?: any
  onExit?: () => void
  modalTopic?: string
  source?: string
  onClickButton?: (onSubmit: () => Promise<void>) => Promise<void>
}

const SendMessage: FC<SendMessageProps> = ({ BenefitEnrollmentView, onExit, modalTopic, source, onClickButton }) => {
  const { personalEmail, email: employeeEmail, partnerEmail } = useSelector(getUserInfo)
  const email = personalEmail || employeeEmail
  const [sentFromPartnerEmail, setSentFromPartnerEmail] = useState(false)
  const [sent, setSent] = useState(false)
  const history = useHistory()
  const dispatch = useDispatch()

  const options = [
    {
      label: email,
      subLabel: personalEmail ? " (your preferred contact email)" : " (your account email)",
      id: email
    },
    {
      label: partnerEmail,
      subLabel: "(your partner's email)",
      id: partnerEmail
    }
  ]

  const formMethods = useForm({
    mode: "onChange",
    defaultValues: {
      message: "",
      defaultMessage: "Member sent a message with no content"
    }
  })
  const { handleSubmit, setError } = formMethods

  const { providerName, topic: topicFromParams } = useParams<{
    providerName: string
    topic: string
  }>()
  let topic = topicFromParams

  const [enrollmentFlowError] = useQueryParam("enrollmentFlowError", StringParam)

  const benefitConfiguration = useSelector(getBenefitConfiguration)

  // eslint-disable-next-line
  // @ts-ignore
  const onSubmit = handleSubmit(async ({ email: emailOption, message, defaultMessage }) => {
    try {
      const email = getInputOrSelectEmail(emailOption)
      const sendFromPartner = email === partnerEmail
      const sendMessage = message ? message : defaultMessage
      setSentFromPartnerEmail(sendFromPartner)
      await client.sendTalkToCarrotMessage(
        sendMessage,
        messageTopicsToSubjectMap[topic],
        sendFromPartner,
        providerName,
        topic
      )
      if (enrollmentFlowError) {
        getHeap().track("EnrollmentFlowSupportRequest", { EventName: enrollmentFlowError })
      }
    } catch (error) {
      if (error.name === HttpErrors.UNAUTHORIZED) {
        dispatch(signOut())
        return
      }
      reportError(error)

      setError("message", {
        type: "internalError",
        message: "Something's not working. Please email your message to support@get-carrot.com."
      })
      return
    }
    setSent(true)
  })

  // Inserting BOOK_APPOINTMENT topic is required since providerName comes from a different route that doesn't have `topic`.
  // Prevents "undefined" topic
  topic = !!providerName ? "bookappointment" : topic
  topic = modalTopic ? modalTopic : topic
  topic = !!source ? source : topic

  const validTopics = Object.keys(messageTopicsToSubjectMap)

  if (!validTopics.includes(topic)) {
    history.push("/talk-to-carrot")
    reportErrorMessage(`Invalid topic passed into SendMessageForm: ${topic}`)
    return null
  }

  // Get page specific heading/description. If it doesn't exist, grab the category page on
  const heading = getPageSpecificHeadingOrFallback(topic, benefitConfiguration)
  const description = getPageSpecificDescriptionOrFallback(topic)

  const context = {
    onSubmit,
    sent,
    options,
    employeeEmail: email,
    personalEmail: personalEmail,
    partnerEmail: partnerEmail,
    sentFromPartnerEmail: sentFromPartnerEmail,
    subtitle: heading,
    description: description,
    onClickButton: onClickButton
  }

  return (
    <SendMessageFormContext.Provider value={context}>
      <FormProvider {...formMethods}>
        {BenefitEnrollmentView ? <BenefitEnrollmentView onExit={onExit} /> : <SendMessageForm {...{ source }} />}
      </FormProvider>
    </SendMessageFormContext.Provider>
  )
}

export { SendMessage, SendMessageFormContext }
